import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { getTheme, setTheme as setThemeStorage, getResolvedTheme } from '@golfauth/utils';
import type { Theme, ResolvedTheme } from '@golfauth/utils';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// With no saved choice, follow the device appearance. Once the user picks
// System / Light / Dark in Settings, the cookie wins.
const NATIVE_DEFAULT: Theme = 'system';

// Dark-mode page-bg matches --color-page-bg in packages/ui/src/tokens.css.
// Light-mode page-bg same — kept as plain hex literals here because the
// status-bar plugin can't read CSS custom properties.
const STATUS_BAR_BG_DARK = '#00112B';
const STATUS_BAR_BG_LIGHT = '#E6EBF3';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => getTheme(NATIVE_DEFAULT));

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(
    () => getResolvedTheme(getTheme(NATIVE_DEFAULT)),
  );

  // Remove one-shot ?theme= from URL after index.html synced cookie (Core → Fitting)
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('theme')) return;
    url.searchParams.delete('theme');
    window.history.replaceState({}, '', url.pathname + url.search + window.location.hash);
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;

    const applyTheme = (newTheme: Theme) => {
      const resolved = getResolvedTheme(newTheme);

      root.classList.remove('light', 'dark');
      root.classList.add(resolved);
      // Brand v1.0 (May 2026): also set data-theme attribute so new
      // components written against the brief's `[data-theme="dark"]`
      // selector work alongside the legacy `.dark` class. Tokens in
      // packages/ui treat both as synonyms.
      root.setAttribute('data-theme', resolved);
      // index.html pre-paint pins an inline hex to avoid a flash; hand it back
      // to the token so later theme switches repaint the document chrome.
      root.style.backgroundColor = 'var(--color-page-bg)';
      setResolvedTheme(resolved);

      // Native iOS/Android status bar follows the resolved theme. Dynamic
      // import keeps the plugin out of the web build — it's only loaded
      // when actually running inside Capacitor. Errors swallowed at
      // every level because:
      //   1. The dynamic import can fail (plugin not in the bundle).
      //   2. The inner setStyle/setBackgroundColor calls can reject
      //      with "plugin not implemented on <platform>" when the
      //      iOS/Android native side hasn't registered StatusBar
      //      (older shipped builds, mid-deploy state, etc.). The
      //      previous `void StatusBar.setStyle(...)` syntax discarded
      //      the value but NOT the rejection — it surfaced as an
      //      `unhandledrejection` and flooded Sentry.
      if (Capacitor.isNativePlatform()) {
        import('@capacitor/status-bar')
          .then(({ StatusBar, Style }) => {
            // Capacitor naming is by background: Style.Dark = light text
            // for dark backgrounds, Style.Light = dark text for light ones.
            StatusBar.setStyle({
              style: resolved === 'dark' ? Style.Dark : Style.Light,
            }).catch(() => {
              /* native plugin not registered — defer to default bar */
            });
            StatusBar.setBackgroundColor({
              color: resolved === 'dark' ? STATUS_BAR_BG_DARK : STATUS_BAR_BG_LIGHT,
            }).catch(() => {
              /* native plugin not registered — defer to default bar */
            });
          })
          .catch(() => {
            // Plugin unavailable at module level — nothing actionable.
          });
      }
    };

    applyTheme(theme);

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        applyTheme('system');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    setThemeStorage(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
